import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { RunEventKey, UpsertRunEventKey } from '@/models/run-event-key.model';
import { getRunEventKey, upsertRunEventKey } from '@/services/run-event-key.service';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, Checkbox } from 'rsuite';
import { toastErrorMessage } from '@/utils/toasterMessage';

const modelSchema = Schema.Model<UpsertRunEventKey>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  isMajor: Schema.Types.BooleanType().isRequired('This field is required.'),
});

const emptyModel: UpsertRunEventKey = {
  id: undefined,
  name: undefined,
  isMajor: false,
};

interface RunEventKeyDrawerProps extends DrawerProps{
  RunEventKeyId?: number,
};

const RunEventKeyDrawer = (props: RunEventKeyDrawerProps) => {
  const { RunEventKeyId:id, open, onClose, ...rest } = props;
  const [formValue, setFormValue] = useState<UpsertRunEventKey>(emptyModel);
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if (id) {
      fetchData();
    } else {
      setFormValue(emptyModel);
    }
  }, [id]);

  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<RunEventKey> = await getRunEventKey(id);
        if (result.success) {
          setFormValue(result.result);
          // console.log(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching RunEvent Key:${errorModel?.errorMessage}`);        
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleDrawerClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue(emptyModel);
    setErrorModel(undefined);
    setLoading(false);
    if(onClose){
      onClose(event);
    }
  };

  const handleSubmit = async event => {
    if (formRef.current && !formRef.current.check()) {
      console.log(formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<RunEventKey> = await upsertRunEventKey(formValue);
      if (result.success) {
        handleDrawerClose(event);
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }
    finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue, 
      ...value 
    }));
  };


  return (
    <Drawer backdrop="static" size="sm" placement="right" 
      open={open}
      onClose={onClose} 
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>{id ? 'Edit a' : 'Add a new'} RunEvent Key</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleDrawerClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='upsert-RunEventKey' fluid ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control name="name" value={formValue.name ?? ''}/>
          </Form.Group>
          <Form.Group>
            <Form.Label>Is Major</Form.Label>
            <Form.Control name="isMajor" accepter={Checkbox} 
              checked={!!formValue.isMajor} 
              onChange={(val, chk) => setFormValue(pre => ({ ...pre, isMajor: chk }))}
            />     
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}
      </Drawer.Body>
    </Drawer>
  );
};

export default RunEventKeyDrawer;
